/**
 * AdminAgreementsAuthoring — /admin/agreements-authoring
 *
 * Fills the 208 documents in the CPG Agreements library that ship
 * without an attorney-drafted starting template. Writes to a new
 * Mongo collection `agreements_admin_templates` (never touches the
 * 49 vendor JS templates).
 *
 * Layout: coverage list on the left (filterable by "missing template
 * only"), author form on the right. Author form has a "Draft with AI"
 * button that hits POST /api/agreements/admin/draft-with-ai to seed
 * the body + placeholders. Nothing saves until the admin explicitly
 * clicks Save. Preview opens the real /agreements/template/{id} route
 * in a new tab so the admin sees what founders will see.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Save, FileText, ExternalLink, RotateCcw } from "lucide-react";

export default function AdminAgreementsAuthoring() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [filter, setFilter] = useState("missing"); // "all" | "missing" | "authored"
  const [query, setQuery] = useState("");

  const [tpl, setTpl] = useState({ body: "", placeholders: [], founderGuide: null });
  const [placeholdersJSON, setPlaceholdersJSON] = useState("[]");
  const [saving, setSaving] = useState(false);
  const [drafting, setDrafting] = useState(false);

  const loadCoverage = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/agreements/admin/coverage");
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("Could not load coverage — admin only.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadCoverage(); }, []);

  const loadTemplate = async (docId) => {
    setSelectedId(docId);
    setTpl({ body: "", placeholders: [], founderGuide: null });
    setPlaceholdersJSON("[]");
    try {
      const { data } = await api.get(`/agreements/admin/template/${docId}`);
      if (data) {
        setTpl({
          body: data.body || "",
          placeholders: data.placeholders || [],
          founderGuide: data.founderGuide || null,
        });
        setPlaceholdersJSON(JSON.stringify(data.placeholders || [], null, 2));
      }
    } catch (e) { /* 404 is fine — new template */ }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (filter === "missing" && (r.hasVendorTemplate || r.hasAdminTemplate)) return false;
      if (filter === "authored" && !r.hasAdminTemplate) return false;
      if (q && !r.docId.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, filter, query]);

  const counts = useMemo(() => {
    let vendor = 0, admin = 0, missing = 0;
    for (const r of rows) {
      if (r.hasVendorTemplate) vendor++;
      else if (r.hasAdminTemplate) admin++;
      else missing++;
    }
    return { total: rows.length, vendor, admin, missing };
  }, [rows]);

  const handleSave = async () => {
    if (!selectedId) return;
    let placeholders = [];
    try {
      placeholders = JSON.parse(placeholdersJSON || "[]");
      if (!Array.isArray(placeholders)) throw new Error("must be an array");
    } catch (e) {
      toast.error("Placeholders JSON is invalid — must be an array.");
      return;
    }
    if (!tpl.body.trim()) {
      toast.error("Body is required.");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/agreements/admin/template/${selectedId}`, {
        body: tpl.body,
        placeholders,
        founderGuide: tpl.founderGuide,
      });
      toast.success("Template saved.");
      await loadCoverage();
    } catch (e) {
      toast.error("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const handleDraft = async () => {
    if (!selectedId) return;
    setDrafting(true);
    try {
      const { data } = await api.post("/agreements/admin/draft-with-ai", {
        docId: selectedId,
        styleHint: "US LLC, plain-language, negotiable",
      });
      setTpl({ body: data.body, placeholders: data.placeholders, founderGuide: null });
      setPlaceholdersJSON(JSON.stringify(data.placeholders || [], null, 2));
      toast.success(
        `Drafted with ${data.model}. Review carefully before saving — this is educational content, not legal advice.`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI draft failed.");
    } finally {
      setDrafting(false);
    }
  };

  const handleDiscard = () => {
    if (!window.confirm("Discard unsaved changes on this document?")) return;
    loadTemplate(selectedId);
  };

  return (
    <AppShell>
      <div className="w-full py-6 space-y-6" data-testid="admin-agreements-authoring">
        <header className="flex flex-wrap items-baseline gap-3 justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Agreements template authoring</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Fill the 208 documents that don't ship with an attorney-drafted starting template.
              Vendor templates are read-only and shown as reference. All admin-authored templates
              are surfaced on the founder-facing <code>/agreements/template/&#123;id&#125;</code> route
              with the standard disclaimer.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap text-xs">
            <Badge variant="secondary" data-testid="cov-total">Total {counts.total}</Badge>
            <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-200 hover:text-emerald-900" data-testid="cov-vendor">
              Vendor {counts.vendor}
            </Badge>
            <Badge className="bg-violet-100 text-violet-800 hover:bg-violet-200 hover:text-violet-900" data-testid="cov-admin">
              Admin-authored {counts.admin}
            </Badge>
            <Badge className="bg-pink-100 text-pink-800 hover:bg-pink-200 hover:text-pink-900" data-testid="cov-missing">
              Missing {counts.missing}
            </Badge>
          </div>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-[minmax(280px,340px)_1fr] gap-6">
          {/* ── Coverage list ── */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Documents</CardTitle>
              <div className="flex gap-1 mt-2">
                {["missing", "authored", "all"].map((k) => (
                  <button
                    key={k}
                    className={`px-2 py-1 rounded text-[11px] uppercase tracking-widest font-semibold ${filter === k ? "bg-primary text-primary-foreground" : "bg-muted"}`}
                    onClick={() => setFilter(k)}
                    data-testid={`coverage-filter-${k}`}
                  >{k}</button>
                ))}
              </div>
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search doc id…"
                className="mt-2 text-sm"
                data-testid="coverage-search"
              />
            </CardHeader>
            <CardContent className="max-h-[600px] overflow-y-auto p-0">
              {loading ? (
                <p className="p-4 text-sm text-muted-foreground">Loading…</p>
              ) : (
                <ul className="divide-y">
                  {filtered.map((r) => (
                    <li key={r.docId}>
                      <button
                        onClick={() => loadTemplate(r.docId)}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-muted ${selectedId === r.docId ? "bg-muted" : ""}`}
                        data-testid={`coverage-row-${r.docId}`}
                      >
                        <span className="block truncate font-medium">{r.docId}</span>
                        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                          {r.hasVendorTemplate ? "Vendor" : r.hasAdminTemplate ? "Admin" : "Missing"}
                        </span>
                      </button>
                    </li>
                  ))}
                  {filtered.length === 0 && (
                    <li className="p-4 text-sm text-muted-foreground">No matches.</li>
                  )}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* ── Author form ── */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="w-4 h-4" />
                {selectedId || "Pick a document on the left"}
              </CardTitle>
              {selectedId && (
                <div className="flex gap-2 mt-2 flex-wrap">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleDraft}
                    disabled={drafting}
                    data-testid="btn-draft-with-ai"
                  >
                    <Sparkles className="w-4 h-4 mr-1" />
                    {drafting ? "Drafting…" : "Draft with AI"}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => window.open(`/agreements/template/${selectedId}`, "_blank")}
                    data-testid="btn-preview"
                  >
                    <ExternalLink className="w-4 h-4 mr-1" /> Preview
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleDiscard}
                    data-testid="btn-discard"
                  >
                    <RotateCcw className="w-4 h-4 mr-1" /> Discard
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSave}
                    disabled={saving}
                    data-testid="btn-save"
                  >
                    <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save"}
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {!selectedId ? (
                <p className="text-sm text-muted-foreground">
                  Choose a document on the left. Missing filter shows the 208 documents that
                  currently have no template.
                </p>
              ) : (
                <>
                  <div className="rounded-md border border-pink-300 bg-pink-50 p-3 text-xs dark:bg-pink-950/30">
                    <strong>Educational content only.</strong> These templates are starting
                    points. Every one must be reviewed by an attorney licensed in the founder's
                    jurisdiction before use. The founder-facing page already carries this
                    disclaimer.
                  </div>
                  <label className="block text-sm">
                    <span className="font-semibold">Body (Markdown + <code>&#123;&#123;placeholder&#125;&#125;</code> tokens)</span>
                    <Textarea
                      value={tpl.body}
                      onChange={(e) => setTpl({ ...tpl, body: e.target.value })}
                      rows={22}
                      className="mt-1 font-mono text-xs"
                      placeholder="PARTIES..."
                      data-testid="author-body"
                    />
                  </label>
                  <label className="block text-sm">
                    <span className="font-semibold">Placeholders (JSON array of &#123;id,label,help&#125;)</span>
                    <Textarea
                      value={placeholdersJSON}
                      onChange={(e) => setPlaceholdersJSON(e.target.value)}
                      rows={6}
                      className="mt-1 font-mono text-xs"
                      data-testid="author-placeholders"
                    />
                  </label>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}
